import { assertNever } from "@shared-kernel/core/types/assert-never";

export enum TRAINING_ITEM_TYPE {
  ONE_ON_ONE_LESSONS = "one_on_one_lessons",
  PROVIDER_BATCH = "provider_batch",
  TEACHER_BATCH = "teacher_batch",
  EXTERNAL_PROVIDER_BATCH = "external_provider_batch",
}

export const TRAINING_ITEM_TYPE_ORDER: Record<TRAINING_ITEM_TYPE, number> = {
  [TRAINING_ITEM_TYPE.ONE_ON_ONE_LESSONS]: 0,
  [TRAINING_ITEM_TYPE.PROVIDER_BATCH]: 1,
  [TRAINING_ITEM_TYPE.TEACHER_BATCH]: 2,
  [TRAINING_ITEM_TYPE.EXTERNAL_PROVIDER_BATCH]: 3,
};

/**
 * Exhaustive check: returns true for batch-like training item types.
 * Adding a new TRAINING_ITEM_TYPE variant will cause a compile error here,
 * forcing the developer to classify the new type.
 */
export function isBatchType(type: TRAINING_ITEM_TYPE): boolean {
  switch (type) {
    case TRAINING_ITEM_TYPE.PROVIDER_BATCH:
    case TRAINING_ITEM_TYPE.TEACHER_BATCH:
    case TRAINING_ITEM_TYPE.EXTERNAL_PROVIDER_BATCH:
      return true;
    case TRAINING_ITEM_TYPE.ONE_ON_ONE_LESSONS:
      return false;
    default:
      return assertNever(type);
  }
}

/**
 * Mails sent before the type values became lowercase, or by the finished external provider batch
 * mail until it was fixed, still carry an uppercase type in their link, and the API refuses it.
 */
export function parseTrainingItemTypeFromUrl(rawType: string | null): TRAINING_ITEM_TYPE | null {
  const normalizedType = rawType?.toLowerCase();
  return Object.values(TRAINING_ITEM_TYPE).find(type => type === normalizedType) ?? null;
}

export enum TRAINING_ITEM_STATUS {
  SCHEDULED = "scheduled",
  RUNNING = "running",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
  REJECTED = "rejected",
  PENDING_ENROLLMENT = "pending_enrollment",
}

export type Question = {
  question: string;
  answer: string;
  order: number;
};

// Student-submitted training items (with questions)
export interface OneOnOneLessonsItem {
  type: TRAINING_ITEM_TYPE.ONE_ON_ONE_LESSONS;
  teachers: { teacherId: string; monthlyLessonsCount: number }[];
  hasUndeterminedCoach: boolean;
  questions: Question[];
  quantifiableObjectives: { text: string }[];
}

export interface ProviderBatchItem {
  type: TRAINING_ITEM_TYPE.PROVIDER_BATCH;
  providerId: string;
  batchId: string;
  questions: Question[];
}

export interface TeacherBatchItem {
  type: TRAINING_ITEM_TYPE.TEACHER_BATCH;
  teacherId: string;
  batchId: string;
  questions: Question[];
}

export interface ExternalProviderBatchItem {
  type: TRAINING_ITEM_TYPE.EXTERNAL_PROVIDER_BATCH;
  externalProviderBatchRequestId: string;
}

export type TrainingItem = OneOnOneLessonsItem | ProviderBatchItem | TeacherBatchItem | ExternalProviderBatchItem;

// Admin-reviewed training items (without questions - questions stay with the original submission)
export interface ReviewedOneOnOneLessonsItem {
  type: TRAINING_ITEM_TYPE.ONE_ON_ONE_LESSONS;
  teachers: Array<{
    teacherId: string;
  }>;
  quantifiableObjectives: { text: string }[];
  internalTraining: {
    id: string;
    description: string;
  };
}

export interface ReviewedProviderBatchItem {
  type: TRAINING_ITEM_TYPE.PROVIDER_BATCH;
  providerId: string;
  batchId: string;
}

export interface ReviewedTeacherBatchItem {
  type: TRAINING_ITEM_TYPE.TEACHER_BATCH;
  teacherId: string;
  batchId: string;
}

export interface ReviewedExternalProviderBatchItem {
  type: TRAINING_ITEM_TYPE.EXTERNAL_PROVIDER_BATCH;
  externalProviderBatchRequestId: string;
}

export type ReviewedTrainingItem =
  | ReviewedOneOnOneLessonsItem
  | ReviewedProviderBatchItem
  | ReviewedTeacherBatchItem
  | ReviewedExternalProviderBatchItem;
